import { createSlice } from '@reduxjs/toolkit'
import moment from 'moment/moment'
import { remove } from 'lodash'

const FLIGHT_TEMPLATE = {
  arrive_airport: {},
  depart_airport: {},
  depart_date: null,
  return_date: null,
}

export const initialState = {
  flightOptions: { 
    flights: [FLIGHT_TEMPLATE], 
    flight_type: 'RT',
    number_adults: 1,
    number_seniors: 0,
    number_youth: 0,
    number_children: 0,
    number_infants_in_seat: 0,
    number_infants_in_lap: 0,
    flight_class: 'E',
    depart_time: '12:00',
    return_time: '12:00',
    ages: {}, 
  },
  hotelOptions: {
    ageDropdownActive: false,
    destination: '',
    revelex_id: '',
    inputValue: '',
    check_in_date: null,
    check_out_date: null,
    rooms: [
      {
        number_adults: 2,
        number_children: 0,
        ages: {},
      },
    ],
    amenities: [],
    priceRange: [0, 1000],
    userRatingRange: [0, 5],
    starRatingRange: [0, 5],
    bookDetails: {},
  },
  longStayOptions: {
    destination: '',
    inputValue: '',
    check_in_date: null,
    check_out_date: null,
    unit_filter: [],
    amenity_filter: [],
    activity_filter: [],
    exclude_all_inclusive_filter: false,
    rooms: 1,
    guests: 2,
    priceRange: [0, 1000],
    userRatingRange: [0, 5],
    starRatingRange: [0, 5],
    bookDetails: {},
  },
  rentalCarOptions: {
    pick_up_location: {},
    drop_off_location: {},
    // pick_up_date: moment(),
    // drop_off_date: moment().add(1, 'days'),
    pick_up_date: null,
    drop_off_date: null,
    dropoff_type: 'same',
  },
  oceanCruiseOptions: {
    search_type: 'cruise_only',
    start_date: null,
    vendor: { vendor_id: -1, code: '', name: '', index: -1 },
    ship_ids: null,
    destination_ids: null,
    cruise_length: 'any',
    destination: { destination_id: '', name: '' },
  },
  riverCruiseOptions: {
    country: { country_code: '', name: '' },
    city: { geolocation_id: '', name: '' },
    river: { waterway_id: '', name: '' },
    ship: { ship_id: '', name: '' },
    search_by: 'country',
  },
  experienceOptions: {
    availabilityID: null,
    // date: moment().format('YYYY-MM-DD'),
    // adult_count: 16,
    adult_count: 2,
    child_list: [],
    availabilityDateRange: [null, null],
    // availabilityDateRange: [moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')],
    selectedOptions: [],
    ageDropdownActive: false,
    bookDetails: {},
  },
  travelQuestionnaire: {
    how_to_plan: '',
    destination_type: [],
    timing_confidence: '',
    departure_window: [],
    departing_from: '',
    traveling_to: '',
    month: '',
    total_travelers: '1',
    total_children: '0',
    ages: '',
    budget: '',
    attractions: '',
    goals: '',
    more_info: '',
    departure_date: null,
    return_date: null,
    destinations_to_avoid: [],
  },
}

const travelSlice = createSlice({
  name: 'travel',
  initialState,
  reducers: {
    setFlightOptions: (state, action) => {
      state.flightOptions = { ...state.flightOptions, ...action.payload }
    },
    clearFlightOptions: state => {
      state.flightOptions = initialState.flightOptions
    },
    setHotelOptions: (state, action) => {
      state.hotelOptions = { ...state.hotelOptions, ...action.payload }
    },
    clearHotelOptions: state => {
      state.hotelOptions = initialState.hotelOptions
    },
    setLongStayOptions: (state, action) => {
      state.longStayOptions = { ...state.longStayOptions, ...action.payload }
    },
    clearLongStayOptions: state => {
      state.longStayOptions = initialState.longStayOptions
    },
    setRentalCarOptions: (state, action) => {
      state.rentalCarOptions = { ...state.rentalCarOptions, ...action.payload }
    },
    clearRentalCarOptions: state => {
      state.rentalCarOptions = initialState.rentalCarOptions
    },
    setOceanCruiseOptions: (state, action) => {
      state.oceanCruiseOptions = { ...state.oceanCruiseOptions, ...action.payload }
    },
    clearOceanCruiseOptions: state => {
      state.oceanCruiseOptions = initialState.oceanCruiseOptions
    },
    setRiverCruiseOptions: (state, action) => {
      state.riverCruiseOptions = { ...state.riverCruiseOptions, ...action.payload }
    },
    clearRiverCruiseOptions: state => {
      state.riverCruiseOptions = initialState.riverCruiseOptions
    },
    setExperienceOptions: (state, action) => {
      state.experienceOptions = { ...state.experienceOptions, ...action.payload }
    },
    clearExperienceOptions: state => {
      state.experienceOptions = initialState.experienceOptions
    },
    addToExperienceOptionList: (state, action) => {
      remove(state.experienceOptions.selectedOptions, o => o.id === action.payload?.id)
      state.experienceOptions.selectedOptions.push(action.payload)
    },
    toggleExperienceOptionListItem: (state, action) => {
      const index = state.experienceOptions.selectedOptions.findIndex(o => o.id === action.payload?.id)
      if (index === -1) {
        state.experienceOptions.selectedOptions.push(action.payload)
      } else if (state.experienceOptions.selectedOptions[index].value === action.payload.value) {
        state.experienceOptions.selectedOptions.splice(index, 1)
      } else {
        state.experienceOptions.selectedOptions.splice(index, 1, action.payload)
      }
    },
    clearExperienceOptionList: (state, action) => {
      state.experienceOptions.selectedOptions = initialState.experienceOptions.selectedOptions
    },
    setTravelQuestionnaire: (state, action) => {
      state.travelQuestionnaire = { ...state.travelQuestionnaire, ...action.payload }
    },
    clearTravelQuestionnaire: (state, action) => {
      state.travelQuestionnaire = initialState.travelQuestionnaire
    },
  },
})

export const {
  setFlightOptions,
  clearFlightOptions,
  setHotelOptions,
  clearHotelOptions,
  setLongStayOptions,
  clearLongStayOptions,
  setRentalCarOptions,
  clearRentalCarOptions,
  setOceanCruiseOptions,
  clearOceanCruiseOptions,
  setRiverCruiseOptions,
  clearRiverCruiseOptions,
  setExperienceOptions,
  clearExperienceOptions,
  addToExperienceOptionList,
  toggleExperienceOptionListItem,
  clearExperienceOptionList,
  setTravelQuestionnaire,
  clearTravelQuestionnaire,
} = travelSlice.actions

export default travelSlice.reducer
